import {timetable_getsByClassesOwner_Api, timetable_getsByOrganization_Api,} from 'apis'
import classNames from 'classnames'
import getTitle from 'helpers/getTitle'
import {formatSimplifyDate} from 'helpers/dateUtils'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext, useMemo, useState} from 'react'
import {BsBuilding, BsCalendar2, BsCalendar2WeekFill, BsChevronDown, BsGeoFill} from 'react-icons/bs'
import {MdMenuBook} from 'react-icons/md'
import {PiMapPinFill} from 'react-icons/pi'
import Toggle from 'components/Toggle'
import usePaginationHelper, {createPagerKey} from 'views/AppLocals/hooks/usePaginationHelper'
import {bindQueryParam, bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import {NullAsDefault, renderIf} from 'views/Shared'
import WorkspaceContext from '../WorkspaceContext'
import {WorkspaceScopeGuide} from '../components/WorkspaceScopeGuide'
import {useLocation} from 'react-use'
import {useRouteSearchParam} from 'envs/_current/history'
import {useMenuHandler} from 'views/Shared/handlers/useMenuHandler'
import {Queries} from 'views/Shared/enums/Queries'
import dayjs from 'dayjs'
import Translate from 'modules/local/Translate'
import {GoDotFill} from "react-icons/go";
import {
    createTimetableFilterActions,
    resolveTimetableStatusFilter,
    TimetableScheduleFilter,
} from 'views/AppLocals/Timetable/TimetableScheduleFilter'

const orgFilterActions = createTimetableFilterActions('org')
const courseFilterActions = createTimetableFilterActions('cls')

const getTimetableDisplayStatus = ({from_date, to_date}) => {
    const now = dayjs()
    const end = to_date ? dayjs(to_date) : dayjs(from_date)
    const start = dayjs(from_date)
    if (end.isBefore(now, 'day')) return 'finished'
    if (start.isAfter(now, 'day')) return 'upcoming'
    return 'ongoing'
}

const TimetableStatusBadge = ({from_date, to_date}) => {
    const t = useTranslate()
    const displayStatus = getTimetableDisplayStatus({from_date, to_date})

    if (displayStatus === 'upcoming') {
        return (
            <span
                style={{padding: '2px 8px'}}
                className="font-bold text-2xs text-blue-500 tracking-widest bg-blue-50 uppercase border border-blue-400 rounded-md">
                {t('upcoming')}
            </span>
        )
    }
    if (displayStatus === 'ongoing') {
        return (
            <span
                style={{padding: '2px 8px'}}
                className="font-bold text-2xs text-green-600 tracking-widest bg-green-50 uppercase border border-green-400 rounded-md">
                {t('active')}
            </span>
        )
    }
    return (
        <span
            style={{padding: '2px 8px'}}
            className="font-bold text-2xs text-gray-500 tracking-widest bg-slate-100 uppercase rounded-md">
            {t('expired')}
        </span>
    )
}

// Danh sách buổi có thể rất dài khi timetable kéo dài nhiều năm (lịch lặp hàng tuần) - mặc định chỉ
// hiện các buổi SẮP TỚI, buổi đã qua gộp lại sau nút "xem buổi đã qua" để tránh rợp mắt + đỡ cuộn.
const TimetableSessionGroup = ({details, location_address, t}) => (
    <div className="flex flex-col gap-3">
        {details.map((detail, i) => {
            const prev = i > 0 ? details[i - 1] : null
            const showDate = detail.start_time && (!prev?.start_time || !dayjs(detail.start_time).isSame(dayjs(prev.start_time), 'day'))

            return (
                <div key={i} className="ml-4 flex flex-col gap-1">
                    {showDate && (
                        <div className="flex items-center gap-1.5">
                            <GoDotFill size={14} className="text-slate-400"/>
                            <span className="font-semibold text-slate-700 tracking-wide">
                                {formatSimplifyDate(detail.start_time, 'medium')}
                            </span>
                        </div>
                    )}
                    <div className="ml-1.5 pl-3 border-l border-color-100 flex flex-col gap-0.5 flex-1 min-w-0">
                        {detail.start_time && (
                            <span className="font-mono text-xs font-medium text-secondary-600">
                                {dayjs(detail.start_time).format('HH:mm')}
                                {detail.end_time
                                    ? ` – ${dayjs(detail.end_time).format('HH:mm')}`
                                    : ''
                                }
                            </span>
                        )}
                        {renderIf(
                            detail.location_address && detail.location_address !== location_address,
                            <span className="flex items-center gap-1 text-xs text-gray-600 tracking-wider">
                                <BsGeoFill size={10} className="flex-shrink-0"/>
                                {detail.location_address}
                            </span>
                        )}
                        {renderIf(
                            detail.notes,
                            <div className="mt-0.5 p-2 background-100 rounded-lg text-xs italic tracking-wider">
                                <span className="font-medium text-color-200">
                                    {`* ${t('note')}: `}
                                </span>
                                <span className="text-color-300 whitespace-pre-line leading-relaxed">
                                    {detail.notes}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            )
        })}
    </div>
)

const TimetableExpandedDetail = ({location_address, notes, timetable_details = []}) => {
    const [showPast, setShowPast] = useState(false)

    const sortedDetails = useMemo(
        () =>
            [...timetable_details].sort((a, b) => {
                if (!a.start_time) return 1
                if (!b.start_time) return -1
                return dayjs(a.start_time).valueOf() - dayjs(b.start_time).valueOf()
            }),
        [timetable_details]
    )
    const upcomingDetails = useMemo(
        () => sortedDetails.filter((d) => d.start_time && !dayjs(d.start_time).isBefore(dayjs(), 'day')),
        [sortedDetails]
    )
    const pastDetails = useMemo(
        () => sortedDetails.filter((d) => !d.start_time || dayjs(d.start_time).isBefore(dayjs(), 'day')),
        [sortedDetails]
    )

    return (
        <Translate>
            {(t) => (
                <div className="px-4 py-3 border-t border-color-50 background flex flex-col gap-2">
                    {renderIf(
                        location_address,
                        <div className="flex items-center gap-1.5">
                            <PiMapPinFill
                                size={13}
                                className="text-blue-400 flex-shrink-0"
                            />
                            <span className="font-bold text-sm text-blue-600 tracking-wide">
                                {location_address}
                            </span>
                        </div>
                    )}
                    {renderIf(
                        notes,
                        <div className="p-2 background-100 rounded-lg text-xs italic tracking-wider">
                            <span className="font-medium text-color-100">
                                {`* ${t('note')}: `}
                            </span>
                            <span className="text-color-300 whitespace-pre-line leading-relaxed">
                                {notes}
                            </span>
                        </div>
                    )}
                    {upcomingDetails.length > 0 ? (
                        <TimetableSessionGroup details={upcomingDetails} location_address={location_address} t={t}/>
                    ) : (
                        <div className="ml-4 py-1 text-xs italic text-color-300">
                            {t('no upcoming sessions')}
                        </div>
                    )}
                    {pastDetails.length > 0 && (
                        <div className="ml-4 flex flex-col gap-3 pt-1 border-t border-color-50">
                            <button
                                type="button"
                                onClick={() => setShowPast((s) => !s)}
                                className="self-start mt-2 flex items-center gap-1.5 text-2xs font-bold text-color-300 hover:text-blue-600 uppercase tracking-wider">
                                <BsChevronDown
                                    size={11}
                                    style={{transform: showPast ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.3s'}}
                                />
                                {showPast ? t('hide past sessions') : `${t('view past sessions')} (${pastDetails.length})`}
                            </button>
                            {showPast && (
                                <div className="opacity-70">
                                    <TimetableSessionGroup details={pastDetails} location_address={location_address} t={t}/>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </Translate>
    )
}

const renderOrgTimetableItem = (item, index) => {
    const {title, group, from_date, to_date, location_address, timetable_details = [], notes} = item ?? {}

    const one_day = !to_date || dayjs(from_date).format('YYYY-MM-DD') === dayjs(to_date).format('YYYY-MM-DD')
    const isExpired = getTimetableDisplayStatus({from_date, to_date}) === 'finished'

    return (
        <Toggle key={index}>
            {(isOpen, toggle) => (
                <div className="transition-all duration-300 relative list-item">
                    <div
                        onClick={(e) => {
                            e.preventDefault()
                            toggle()
                        }}
                        className="p-4 flex items-start gap-3 cursor-pointer select-none">
                        <div
                            className={classNames(
                                'w-10 h-10 rounded-lg flex flex-center flex-shrink-0',
                                isExpired ? 'background-200' : 'bg-secondary-50'
                            )}>
                            <BsCalendar2WeekFill size={18} className={isExpired ? 'text-color-500' : 'text-secondary-500'}/>
                        </div>
                        <div className="flex flex-col flex-1 min-w-0 gap-1">
                            <span className={classNames(
                                'text-base leading-tight',
                                isExpired ? 'font-semibold text-color-300 line-through' : isOpen ? 'font-bold text-secondary-600' : 'font-bold text-gray-800'
                            )}>
                                {title}
                            </span>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                <span className="flex items-center gap-1 font-medium text-xs text-color-300 tracking-wider">
                                    <BsCalendar2 size={11} className="text-color-500 flex-shrink-0"/>
                                    {one_day
                                        ? formatSimplifyDate(from_date)
                                        : `${formatSimplifyDate(from_date)} - ${formatSimplifyDate(to_date)}`
                                    }
                                </span>
                                {group?.title && (
                                    <span className="flex items-center gap-1 font-medium text-xs text-color-300 tracking-wider">
                                        <BsBuilding size={11} className="text-color-500 flex-shrink-0"/>
                                        {group.title}
                                    </span>
                                )}
                                <div className="flex-1"/>
                                <TimetableStatusBadge from_date={from_date} to_date={to_date}/>
                            </div>
                        </div>
                        <div className="text-color-300 flex-shrink-0 mt-1">
                            <BsChevronDown size={14} style={{transform: isOpen ? 'rotate(-90deg)' : ''}}/>
                        </div>
                    </div>
                    {isOpen && (
                        <TimetableExpandedDetail
                            location_address={location_address}
                            notes={notes}
                            timetable_details={timetable_details}
                        />
                    )}
                </div>
            )}
        </Toggle>
    )
}

const renderCourseTimetableItem = (item, index) => {
    const {course_class, subject, from_date, to_date, location_address, timetable_details = [], notes} = item ?? {}

    const one_day = !to_date || dayjs(from_date).format('YYYY-MM-DD') === dayjs(to_date).format('YYYY-MM-DD')
    const isExpired = getTimetableDisplayStatus({from_date, to_date}) === 'finished'
    const classTitle = getTitle(course_class)
    const subjectTitle = getTitle(subject)

    return (
        <Toggle key={index}>
            {(isOpen, toggle) => (
                <div className="transition-all duration-300 relative list-item">
                    <div
                        onClick={(e) => {
                            e.preventDefault()
                            toggle()
                        }}
                        className="p-4 flex items-start gap-3 cursor-pointer select-none">
                        <div
                            className={classNames(
                                'w-10 h-10 rounded-lg flex flex-center flex-shrink-0',
                                isExpired ? 'background-200' : 'bg-secondary-50'
                            )}>
                            <MdMenuBook size={18} className={isExpired ? 'text-color-500' : 'text-secondary-500'}/>
                        </div>
                        <div className="flex flex-col flex-1 min-w-0 gap-1">
                            <span className={classNames(
                                'text-base leading-tight',
                                isExpired ? 'font-semibold text-color-300 line-through' : isOpen ? 'font-bold text-secondary-600' : 'font-bold text-gray-800'
                            )}>
                                {subjectTitle || classTitle}
                            </span>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                <span className="flex items-center gap-1 font-medium text-xs text-color-300 tracking-wider">
                                    <BsCalendar2 size={11} className="text-color-500 flex-shrink-0"/>
                                    {one_day
                                        ? formatSimplifyDate(from_date)
                                        : `${formatSimplifyDate(from_date)} - ${formatSimplifyDate(to_date)}`
                                    }
                                </span>
                                {classTitle && (
                                    <span className="flex items-center gap-1 font-medium text-xs text-color-300 tracking-wider">
                                        <MdMenuBook size={11} className="text-color-500 flex-shrink-0"/>
                                        {classTitle}
                                    </span>
                                )}
                                <div className="flex-1"/>
                                <TimetableStatusBadge from_date={from_date} to_date={to_date}/>
                            </div>
                        </div>
                        <div className="text-color-300 flex-shrink-0 mt-1">
                            <BsChevronDown size={14} style={{transform: isOpen ? 'rotate(-90deg)' : ''}}/>
                        </div>
                    </div>
                    {isOpen && (
                        <TimetableExpandedDetail
                            location_address={location_address}
                            notes={notes}
                            timetable_details={timetable_details}
                        />
                    )}
                </div>
            )}
        </Toggle>
    )
}

// Trang này KHÔNG có nút "tạo" vì lịch trình thuộc sở hữu của 1 Nhóm/Đơn vị hoặc 1 Lớp học cụ thể
// (owner_type/owner_id) - đây chỉ là danh sách tổng hợp (read-only) gộp từ mọi Nhóm/Lớp trong Cộng
// đoàn. Callout giải thích + trỏ đúng nơi có quyền tạo, tránh user tìm nút tạo mãi không thấy.
// Component dùng chung với các trang tổng hợp khác (VD: tab Khóa học ở Truyền thông > Thông báo) -
// xem WorkspaceScopeGuide.

const WorkspaceOrgTimetableList = ({organization_id}) => {
    const location = useLocation()

    const keyword = useRouteSearchParam(orgFilterActions.keyword)
    const from_time_open = useRouteSearchParam(orgFilterActions.from_time_open)
    const to_time_open = useRouteSearchParam(orgFilterActions.to_time_open)
    const include_expired = useRouteSearchParam(orgFilterActions.include_expired)
    const status = resolveTimetableStatusFilter(include_expired)

    const refresh_deps = useMemo(
        () => [
            organization_id,
            keyword,
            from_time_open,
            to_time_open,
            status,
            `refreshToken=${_.get(location.state, 'refreshToken')}`,
        ],
        [organization_id, keyword, from_time_open, to_time_open, status, location.state]
    )

    const [pager_widget] = usePaginationHelper({
        pagerInfo: {
            RenderEntity: React.Fragment,
            PaginationMeta: NullAsDefault,
            apiInfo: timetable_getsByOrganization_Api,
            query: bindQueryParam({id: organization_id}),
            renderItem: renderOrgTimetableItem,
            renderWrap: (child) => (
                <div className="mt-3 flex flex-col divide-y divide-gray-200 list-item-workspace">
                    {child}
                </div>
            ),
        },
        SortByWidget: NullAsDefault,
        renderSortBy: NullAsDefault,
        isToggleDefault: true,
        query_params: orgFilterActions,
        onInit: ({onFilterChange = NullAsDefault}) => {
            onFilterChange({keyword, from_time_open, to_time_open, status})
        },
        filter_params: {keyword, from_time_open, to_time_open, status},
        dependencies: [organization_id, keyword, from_time_open, to_time_open, status],
        withKey: createPagerKey(refresh_deps),
        AdvancedFilter: TimetableScheduleFilter,
        renderHeader: ([searchBar]) => searchBar,
    })

    return (
        <div className="flex flex-col">
            {pager_widget}
            <div className="h-16"/>
        </div>
    )
}

const WorkspaceCourseTimetableList = ({organization_id}) => {
    const location = useLocation()

    const keyword = useRouteSearchParam(courseFilterActions.keyword)
    const from_time_open = useRouteSearchParam(courseFilterActions.from_time_open)
    const to_time_open = useRouteSearchParam(courseFilterActions.to_time_open)
    const include_expired = useRouteSearchParam(courseFilterActions.include_expired)
    const status = resolveTimetableStatusFilter(include_expired)

    const refresh_deps = useMemo(
        () => [
            organization_id,
            keyword,
            from_time_open,
            to_time_open,
            status,
            `refreshToken=${_.get(location.state, 'refreshToken')}`,
        ],
        [organization_id, keyword, from_time_open, to_time_open, status, location.state]
    )

    const [pager_widget] = usePaginationHelper({
        pagerInfo: {
            RenderEntity: React.Fragment,
            PaginationMeta: NullAsDefault,
            apiInfo: timetable_getsByClassesOwner_Api,
            query: bindQueryParams([{id: organization_id}, {type: 'organization'}]),
            renderItem: renderCourseTimetableItem,
            renderWrap: (child) => (
                <div className="mt-3 flex flex-col divide-y divide-gray-200 list-item-workspace">
                    {child}
                </div>
            ),
        },
        SortByWidget: NullAsDefault,
        renderSortBy: NullAsDefault,
        isToggleDefault: true,
        query_params: courseFilterActions,
        onInit: ({onFilterChange = NullAsDefault}) => {
            onFilterChange({keyword, from_time_open, to_time_open, status})
        },
        filter_params: {keyword, from_time_open, to_time_open, status},
        dependencies: [organization_id, keyword, from_time_open, to_time_open, status],
        withKey: createPagerKey(refresh_deps),
        AdvancedFilter: TimetableScheduleFilter,
        renderHeader: ([searchBar]) => searchBar,
    })

    return (
        <div className="flex flex-col">
            {pager_widget}
            <div className="h-16"/>
        </div>
    )
}

const TabKeys = Object.freeze({
    ORGANIZATION: 'org-timetable',
    COURSE: 'course-timetable',
})

const tabRoutes = [
    {key: TabKeys.ORGANIZATION, path: TabKeys.ORGANIZATION},
    {key: TabKeys.COURSE, path: TabKeys.COURSE},
]

const WorkspaceActivityCalendar = () => {
    const t = useTranslate()
    const {organization_id} = useContext(WorkspaceContext)

    const {activeKey, onMenuSelect} = useMenuHandler({
        routes: tabRoutes,
        actionName: Queries.TAB,
        defaultKey: TabKeys.ORGANIZATION,
    })

    // Label tab phải nêu rõ PHẠM VI dữ liệu (Nhóm/Đơn vị vs Lớp học) - dùng chữ "Cộng đoàn" chung
    // chung dễ gây hiểu lầm vì cả trang này vốn đã nằm trong 1 Cộng đoàn cụ thể rồi.
    const tabs = [
        {key: TabKeys.ORGANIZATION, label: 'groups & units'},
        {key: TabKeys.COURSE, label: 'class timetable title'},
    ]

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex flex-col flex-1">
                    <h2 className="font-bold text-xl text-gray-800 tracking-wide">
                        {t('activity schedule')}
                    </h2>
                    <div className="mt-1 w-20 h-1 rounded-full bg-gray-400"/>
                    <span className="mt-2 text-xs text-color-300 tracking-wide">
                        {t('activity schedule description')}
                    </span>
                </div>
            </div>
            <div className="mr-12 flex gap-3 z-3">
                {tabs.map(({key, label}) => (
                    <button
                        key={key}
                        onClick={() => onMenuSelect(key)}
                        style={{ minHeight: 40 }}
                        className={classNames(
                            'flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-medium transition-all duration-300',
                            activeKey === key
                                ? 'bg-secondary-500 text-white'
                                : 'background border border-color-50 hover:border-secondary-500 text-color-200 hover:bg-secondary-50 hover:text-secondary-500 shadow-sm'
                        )}>
                        {t(label)}
                    </button>
                ))}
            </div>
            {activeKey === TabKeys.ORGANIZATION ? (
                <WorkspaceScopeGuide
                    description="activity schedule org description"
                    guideKey="create schedule guide organization"
                    linkTo="/workspace/member?rel=org-groups"
                    linkLabel="group list"
                />
            ) : (
                <WorkspaceScopeGuide
                    description="activity schedule course description"
                    guideKey="create schedule guide course"
                    linkTo="/workspace/course?rel=course-class"
                    linkLabel="class list"
                />
            )}
            <div>
                {activeKey === TabKeys.ORGANIZATION
                    ? <WorkspaceOrgTimetableList organization_id={organization_id}/>
                    : <WorkspaceCourseTimetableList organization_id={organization_id}/>
                }
            </div>
        </div>
    )
}

export default WorkspaceActivityCalendar
